import React, { useEffect, FC } from 'react';
import useMount from 'react-use/lib/useMount';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { LocalizeProvider, LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { useLocalStorage } from '@tradingblock/storage';
import { getLocalizeOptions, allTranslations, LanguageType } from '@tradingblock/types';
import { AlertsContainer, useTracking } from '@tradingblock/components';
import { DataInitializer } from './data/Initializer';
import { useStateSelector } from './data/global/dataSelectors';
import { RootContentPage } from './pages/RootContentPage';
import { Feed } from './data/feed/Feed';
import { AuthenticationPage } from './pages/AuthenticationPage';
import { LogoutPage } from './pages/LogoutPage';
import { AdminRouter } from './pages/admin/AdminRouter';
import { NoAccountPage } from './pages/NoAccountPage';
import { AppStoreProvider } from './AppStoreProvider';
import { usePageTitle } from './hooks/usePageTitle';
import Config from './config';
import { CashieringUpdatesPoll } from './components/cashiering/CashieringUpdatesPoll';
import { UpdateConfirmationPage } from './pages/UpdateConfirmationPage';
import ReactGA from 'react-ga4';
import config from './config';
import WebSocketInitializer from './data/feed/WebSocketInitializer';
import NewsWebSocketInitializer from './data/feed/NewsWebSocketInitializer';
import * as Sentry from '@sentry/react';
import OrderStatusWebSocketInitializer from './data/feed/OrderStatusWebSocketInitializer';
import ExpiringTokensWebSocketInitializer from './data/feed/ExpiringTokensWebSocketInitializer';
import CashieringWebSocketInitializer from './data/feed/CashieringWebSocketInitializer';
import LoginCountWebSocketInitializer from './data/feed/LoginCountWebSocketInitializer';
import QuotesWebSocketInitializer from './data/feed/QuotesWebSocketInitializer';
import SpreadsWebSocketInitializer from './data/feed/SpreadsWebSocketInitializer';
import { useFeatureFlag } from '@tradingblock/components';
import { FeatureFlagWrapper } from './components/FeatureFlagWrapper';

const TRACKING_ID = config.isVirtual ? config.ga4VTTrackingCode : config.ga4TrackingCode;
if (TRACKING_ID) {
  ReactGA.initialize(TRACKING_ID);
}

const AppRouter: React.FunctionComponent<{}> = () => {
  usePageTitle();

  const tokenFromState = useStateSelector(s => s.auth.uiApiToken);
  return (
    <>
      <BrowserRouter basename={Config.subdirectory}>
        <Switch>
          <Route path="/account-update/:confirm?" exact component={UpdateConfirmationPage} />
          <Route path="/auth" exact component={AuthenticationPage} />
          <Route path="/logout" exact component={LogoutPage} />
          <Route path="/no-account" exact component={NoAccountPage} />
          <Route path="/admin/:path(.*)?" component={tokenFromState ? AdminRouter : AuthenticationPage} />
          <Route path="/:path(.*)" component={tokenFromState ? RootContentPage : AuthenticationPage} />
        </Switch>
      </BrowserRouter>
      {tokenFromState && <CashieringUpdatesPoll />}
    </>
  );
};

const AppContent: React.FC<LocalizeContextProps> = ({ initialize, addTranslationForLanguage, children }) => {
  const { isEnabled: useLofiOrderUpdates } = useFeatureFlag('lofi-order-updates');
  const { isEnabled: useLofiExpiringTokens } = useFeatureFlag('lofi-expiring-tokens');
  const { isEnabled: useLofiCashiering } = useFeatureFlag('lofi-cashiering');
  const { isEnabled: useLofiLoginCount } = useFeatureFlag('lofi-login-count');

  useMount(() => {
    initialize(getLocalizeOptions([LanguageType.English]));
    addTranslationForLanguage(allTranslations[LanguageType.English], LanguageType.English);
  });
  return (
    <Feed>
      <AlertsContainer />
      <DataInitializer />
      <WebSocketInitializer />
      <NewsWebSocketInitializer />
      {useLofiOrderUpdates && <OrderStatusWebSocketInitializer />}
      {useLofiExpiringTokens && <ExpiringTokensWebSocketInitializer />}
      {useLofiCashiering && <CashieringWebSocketInitializer />}
      {useLofiLoginCount && <LoginCountWebSocketInitializer />}
      <QuotesWebSocketInitializer />
      <SpreadsWebSocketInitializer />
      {children}
      {/* <AppRouter /> */}
    </Feed>
  );
};

const AppContentWithLocalize = withLocalize(AppContent);

const AppGATracking: FC<{}> = ({ children }) => {
  // const { initTracking } = useTracking(Config.gaUaTrackingCode, Config.ga4TrackingCode);
  // useMount(() => initTracking());
  return <>{children}</>;
};

const App: React.FC = () => {
  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
    // rg4js('trackEvent', {
    //   type: 'pageView',
    //   path: window.location.pathname
    // });
  }, []);

  return (
    <>
      <AppGATracking />
      <AppStoreProvider>
        <FeatureFlagWrapper>
          <useLocalStorage.Provider>
            <LocalizeProvider>
              <AppContentWithLocalize>
                <AppRouter />
              </AppContentWithLocalize>
            </LocalizeProvider>
          </useLocalStorage.Provider>
        </FeatureFlagWrapper>
      </AppStoreProvider>
    </>
  );
};

export default App;
